import React from 'react';
import { supabase } from './supabase';
import { useConfirm } from '../components/ConfirmDialog';
import { RescheduleModal } from '../components/RescheduleModal';
import { PaymentActionSheet } from '../components/PaymentActionSheet';

/**
 * useBookingActions — the single source of truth for booking lifecycle
 * actions. Called by BOTH the "..." kebab menu AND the booking detail
 * panel, so the two surfaces can never disagree on gating logic, copy,
 * or the underlying API call.
 *
 * Returns:
 *   {
 *     can:  { complete, noShow, cancel, reopen, markDeposit, markFull, unmark, reschedule },
 *     busy: boolean,                      // any action currently mid-flight
 *     confirmNode,                        // <ConfirmDialog>, MUST be rendered by the caller
 *     markCompleted, markNoShow,          // each returns a Promise, ok=true on success
 *     cancelBooking, reopen,
 *     markDepositPaid, markPaidInFull, undoPayment,
 *   }
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fires `PATCH /api/bookings/{id}/status`.
const patchStatus = async (bookingId, status, authToken) => {
  const res = await fetch(`${BACKEND_URL}/api/bookings/${bookingId}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
    body: JSON.stringify({ status }),
  });
  if (!res.ok) throw new Error(`Status update failed (${res.status}): ${await res.text()}`);
  return res.json();
};

// Fires `POST /api/bookings/{id}/payment`.
const patchPayment = async (bookingId, patch, authToken) => {
  const res = await fetch(`${BACKEND_URL}/api/bookings/${bookingId}/payment`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`Payment update failed (${res.status}): ${await res.text()}`);
  return res.json();
};

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
const fmtMoney = (n) => `£${Number(n || 0).toFixed(0)}`;

export const useBookingActions = (booking, onChanged, perms) => {
  // `perms` narrows what actions a signed-in BARBER can trigger. Pass
  // undefined (or leave off) for shop_admin / master_admin callers and
  // every action stays available — matches the server guards which
  // apply the same "narrow, never widen" rule.
  const canManageBookings = perms?.canManageBookings !== false;   // default true
  const canTakePayments   = perms?.canTakePayments   !== false;   // default true

  const [busy, setBusy] = React.useState(false);
  const [confirmNode, confirm] = useConfirm();
  const [rescheduleOpen, setRescheduleOpen] = React.useState(false);
  // Payment sheet is a two-phase flow: caller opens it via markCompleted /
  // markPaidInFull, sheet's onPick fires the record + follow-up action.
  // `paySheet.mode` is one of:
  //   'complete-collect'  — collect balance and mark completed
  //   'collect-only'      — collect balance, do not mark completed
  const [paySheet, setPaySheet] = React.useState(null);   // { mode } | null

  // Outstanding balance in £. Mirrors the identical helper in the shop
  // Dashboard so both surfaces agree on when to open the sheet.
  const owingFor = React.useCallback((b) => {
    if (!b) return 0;
    if (b.paid_in_full) return 0;
    const price = Number(b.service?.price ?? 0);
    const deposit = Number(b.deposit_amount ?? 0);
    return Math.max(0, price - (b.deposit_paid ? deposit : 0));
  }, []);

  const can = React.useMemo(() => {
    if (!booking) return {};
    const closed = ['completed', 'no_show', 'cancelled'].includes(booking.status);
    const inFuture = new Date(booking.ends_at) > new Date();
    return {
      // Booking-lifecycle actions require can_manage_bookings.
      complete:   !closed && canManageBookings,
      noShow:     !closed && canManageBookings,
      cancel:     !closed && inFuture && canManageBookings,
      reopen:     closed && canManageBookings,
      reschedule: !closed && canManageBookings,
      // Payment actions require can_take_payments.
      markDeposit: !closed && !booking.deposit_paid && !booking.paid_in_full && canTakePayments,
      markFull:    !closed && !booking.paid_in_full && canTakePayments,
      unmark:      !closed && (booking.paid_in_full || booking.deposit_paid) && canTakePayments,
    };
  }, [booking, canManageBookings, canTakePayments]);

  // "bob's 13:27 Cut & Beard Trim"
  const describe = React.useCallback(() => {
    if (!booking) return '';
    const who = booking.customer?.name || 'this client';
    const when = fmtTime(booking.starts_at);
    const what = booking.service?.name || 'appointment';
    return `${who}\u2019s ${when} ${what}`;
  }, [booking]);

  // Consequence sentence for a Cancel confirmation. Only counts money
  // captured via Stripe (has a payment_intent) toward the refund
  // total. Cash + in-shop-card balances are surfaced as "to refund in
  // shop" because we cannot auto-refund them.
  const describeCancelConsequence = React.useCallback(() => {
    if (!booking) return '';
    const hasStripePI = !!booking.stripe_payment_intent_id;
    const stripeRefund = hasStripePI
      ? (booking.paid_in_full
          ? Number(booking.service?.price || 0)
          : booking.deposit_paid
            ? Number(booking.deposit_amount || 0)
            : 0)
      : 0;
    const inShopPaid = Number(booking.balance_paid_amount || 0);
    const parts = ['The client will be notified'];
    if (stripeRefund > 0) parts.push(`${fmtMoney(stripeRefund)} refunded to their card`);
    if (inShopPaid > 0)   parts.push(`${fmtMoney(inShopPaid)} to refund in shop`);
    return parts.join(' and ') + '.';
  }, [booking]);

  // -- Internal runners -------------------------------------------------
  // Broadcasts to any listening surface (currently the shop Dashboard)
  // that a booking has changed and its KPIs should be refetched. Fires
  // once per successful mutation, best-effort no-op if window is gone.
  const _emitBookingChanged = () => {
    try { window.dispatchEvent(new CustomEvent('instacut:booking-changed')); } catch { /* SSR / test env */ }
  };

  const _runStatus = async (status) => {
    setBusy(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');
      await patchStatus(booking.id, status, session.access_token);
      onChanged?.();
      _emitBookingChanged();
      return true;
    } catch (e) {
      alert(e.message || 'Could not update the booking status.');
      return false;
    } finally {
      setBusy(false);
    }
  };
  const _runPayment = async (patch) => {
    setBusy(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');
      await patchPayment(booking.id, patch, session.access_token);
      onChanged?.();
      _emitBookingChanged();
      return true;
    } catch (e) {
      alert(e.message || 'Could not update the payment.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  // -- Public actions (each returns Promise<boolean>) -------------------
  // Mark completed. If there's an outstanding balance, opens the
  // Cash/Card sheet FIRST so we can capture how the money changed
  // hands at the chair. Only after the sheet resolves (or the barber
  // taps "still owing") do we PATCH status to 'completed'.
  const markCompleted = async () => {
    const owe = owingFor(booking);
    // If there's money owing AND the caller is allowed to take
    // payments, open the Cash/Card sheet first so we capture how the
    // money changed hands. Barbers without can_take_payments skip the
    // sheet entirely — the booking just completes with the balance
    // still outstanding (their shop admin will collect later).
    if (owe > 0 && canTakePayments) {
      setPaySheet({ mode: 'complete-collect' });
      return true;
    }
    const ok = await confirm({
      title: `Mark ${describe()} as completed?`,
      body: owe > 0 && !canTakePayments
        ? 'Your account isn\u2019t permitted to record payments, so the booking will complete with the balance still outstanding.'
        : 'The booking will move to the completed list and count toward this client\u2019s visit history.',
      confirmLabel: 'Mark completed',
      cancelLabel: 'Cancel',
    });
    if (!ok) return false;
    return _runStatus('completed');
  };

  const markNoShow = async () => {
    const ok = await confirm({
      title: `Mark ${describe()} as no-show?`,
      body: 'The slot stays used and this client\u2019s no-show count goes up. This can be undone by reopening the booking.',
      danger: true,
      confirmLabel: 'Mark no-show',
      cancelLabel: 'Cancel',
    });
    if (!ok) return false;
    return _runStatus('no_show');
  };

  const cancelBooking = async () => {
    const ok = await confirm({
      title: `Cancel ${describe()}?`,
      body: describeCancelConsequence(),
      danger: true,
      confirmLabel: 'Cancel booking',
      cancelLabel: 'Keep booking',
    });
    if (!ok) return false;

    setBusy(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');
      const res = await fetch(`${BACKEND_URL}/api/bookings/${booking.id}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`Cancel failed (${res.status}): ${t}`);
      }
      const body = await res.json();
      // Surface refund state to the shop so a Stripe failure is never
      // silent. The booking is still cancelled in every case.
      if (body?.refund?.failed) {
        alert(
          `Booking cancelled, but the £${(body.refund.amount_pence/100).toFixed(0)} refund FAILED.\n\n` +
          `Reason: ${body.refund.error || 'unknown'}\n\n` +
          `The client has been notified of the cancellation. Please chase the refund manually.`
        );
      } else if (body?.manual_refund_due?.amount) {
        alert(
          `Booking cancelled. Please refund £${body.manual_refund_due.amount.toFixed(0)} to the client ` +
          `in shop (paid by ${body.manual_refund_due.method}).`
        );
      }
      onChanged?.();
      _emitBookingChanged();
      return true;
    } catch (e) {
      alert(e.message || 'Could not cancel the booking.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  // Reopen is a recovery, not destructive — no confirm needed.
  const reopen = async () => _runStatus('confirmed');

  // Mark deposit paid — used when someone hand-took the deposit (rare
  // path; Stripe deposits auto-flag via webhook). No method capture
  // here to avoid conflating with `balance_paid_method` which the
  // backend treats as "paid in full". Kept as a bare flag flip.
  const markDepositPaid = async () => _runPayment({ deposit_paid: true });

  // Mark paid-in-full — always opens the Cash/Card sheet so we record
  // which method cleared the balance. Never fires the old bare
  // `{ paid_in_full: true }` write.
  const markPaidInFull = async () => {
    setPaySheet({ mode: 'collect-only' });
    return true;
  };

  const undoPayment = async () => _runPayment({ paid_in_full: false, deposit_paid: false });

  // Reschedule is a two-step flow (picker + confirm) rendered by the
  // RescheduleModal below. This method just flips the flag; the modal
  // handles its own confirm dialog + SMS side-effect via the backend.
  const openReschedule = () => setRescheduleOpen(true);

  const rescheduleNode = rescheduleOpen && booking ? (
    <RescheduleModal
      booking={booking}
      onClose={() => setRescheduleOpen(false)}
      onSaved={() => {
        setRescheduleOpen(false);
        onChanged?.();
        _emitBookingChanged();
      }}
    />
  ) : null;

  // Cash/Card sheet — the SINGLE place any Diary payment is captured.
  // Wired to booking.balance for the collect-only path, or owingFor(booking)
  // for the complete-collect path. Both paths record method+amount via
  // POST /bookings/{id}/payment; complete-collect additionally PATCHes
  // status → 'completed' afterwards.
  const paymentSheetNode = paySheet && booking ? (
    <PaymentActionSheet
      active
      header={paySheet.mode === 'collect-only' ? 'Record payment' : 'Complete booking'}
      hint="Which method did the customer pay with?"
      customerName={booking.customer?.name || '—'}
      serviceName={booking.service?.name || '—'}
      outstanding={paySheet.mode === 'collect-only'
        ? (booking.paid_in_full ? 0 : Number(booking.service?.price ?? 0))
        : owingFor(booking)}
      busy={busy}
      onPick={async (method) => {
        const amount = paySheet.mode === 'collect-only'
          ? Number(booking.service?.price ?? 0)
          : owingFor(booking);
        const ok = await _runPayment({ balance_paid_method: method, balance_paid_amount: amount });
        if (ok && paySheet.mode === 'complete-collect') {
          await _runStatus('completed');
        }
        setPaySheet(null);
      }}
      onSkip={paySheet.mode === 'complete-collect'
        ? async () => { setPaySheet(null); await _runStatus('completed'); }
        : null}
      skipLabel={paySheet.mode === 'complete-collect' ? 'Not yet — still owing' : null}
      skipHint={paySheet.mode === 'complete-collect' ? 'Mark complete but leave the balance outstanding' : null}
      onClose={() => setPaySheet(null)}
    />
  ) : null;

  return {
    can,
    busy,
    confirmNode,
    rescheduleNode,
    paymentSheetNode,
    markCompleted,
    markNoShow,
    cancelBooking,
    reopen,
    markDepositPaid,
    markPaidInFull,
    undoPayment,
    openReschedule,
  };
};
